// Group quiz data model.
//
// Groups let multiple users take the same locked question set and compare
// results. After the D1 cutover the data lives in:
//
//   groups                                 - one row per group
//   group_members                          - one row per (group_id, uid)
//   group_answers                          - completed-quiz contributions
//   group_predictions                      - predict-the-group submissions
//
// Identity: members are keyed by the votely_uid cookie uid (issued by
// middleware.ts on first visit).

// Cookie-uid era doesn't ship firebase/firestore. Keep the duck-typed
// shape the existing call sites use: a value that responds to
// toMillis() so isGroupExpired / age comparisons keep compiling.
export type Timestamp = { toMillis(): number };

export type GroupStatus = 'open' | 'closed' | 'expired';

export type MemberStatus = 'taking' | 'completed';

export type GroupCreationSource = 'direct' | 'quiz_results' | 'group_results' | 'group_reveal' | 'group_retake';
export type GroupCreationAttribution = { parentGroupId?: string; creationSource: GroupCreationSource };

// Hard cap on group size. Enforced in firestore.rules and on the client.
export const GROUP_MAX_MEMBERS = 8;

// Minimum completed members before reveal unlocks. Used by both client UI
// and security rules (a member's answers/cubePosition only become readable
// to others once the group reaches this threshold).
export const GROUP_REVEAL_MIN = 2;

// Group lifetime. After this, status flips to 'expired' and joins/edits are
// blocked. Enforced by a scheduled job (P3); rules just check the timestamp.
export const GROUP_TTL_DAYS = 7;

// Display name length cap.
export const GROUP_DISPLAY_NAME_MAX = 40;

// Locked-question-set length cap (sanity bound for rules; the actual set is
// the 12 short-quiz questions).
export const GROUP_QUESTION_IDS_MAX = 50;

export interface Group {
  groupId: string;
  name?: string;
  // hostUserId is a per-group PSEUDONYM (m-xxxxxxxxxx), not a raw uid - the API
  // never returns raw uids. Compare it against member/answer `userId` (also
  // pseudonyms) to find the host's row; do NOT compare it against a real uid.
  hostUserId: string;
  // Server-computed identity for the current caller, so the client never needs
  // a raw uid to self-identify. `you` is this caller's pseudonym (matches their
  // own row's `userId`); `isHost` is whether they are the group host.
  you?: string | null;
  isHost?: boolean;
  status: GroupStatus;
  createdAt: Timestamp;
  expiresAt: Timestamp;
  // Locked at creation. Every member answers exactly these question IDs,
  // regardless of which quiz type they would otherwise have taken.
  questionIds: string[];
  locale: 'en' | 'de';
  // Denormalized counters. Kept in sync via batched writes from join +
  // completion flows. Cheaper than counting the members subcollection.
  memberCount: number;
  completedCount: number;
  // Set when the reveal threshold is first crossed; once present, member
  // answer edits are blocked.
  revealedAt?: Timestamp;
  // Set by the notify-group-reveals cron the first time it sends reveal-
  // ready emails for this group. Acts as the idempotency key: groups with
  // this field set are skipped on subsequent runs.
  revealNotifiedAt?: Timestamp;
  // Set by the notify-group-retakes cron the first time it sends "retake
  // with the same crew" reminders for this group, ~90 days after the
  // initial reveal. Same idempotency pattern as revealNotifiedAt: once
  // set, the group is filtered out of subsequent retake-cron runs.
  retakeNotifiedAt?: Timestamp;
  // If this group was created via /group/new?retake=<id>, retakeOf
  // points back to the original group. Lets the reveal render a
  // "compared to last time" overlay showing centroid shift since the
  // original. Display-name based member matching is fragile but the
  // group centroid comparison works regardless.
  retakeOf?: string;
  // Compare redesign. 'pair' = 1:1, 'group' = N-way (default), 'broadcast'
  // = anonymous. maxParticipants caps size (2 for pair, null = unlimited).
  mode?: 'pair' | 'group' | 'broadcast';
  maxParticipants?: number | null;
  // Comparison surface this group locks to: 'short' = the 12 short-quiz
  // questions, 'long' = the 36 Phase-1 core questions. Immutable post-create.
  minQuizType?: 'short' | 'long';
  creatorQuizType?: 'short' | 'long';
}

// Public-to-group member info. Readable by any authenticated user who is a
// member of the group. Pre-reveal lobby shows this.
export interface GroupMember {
  userId: string;
  displayName: string;
  status: MemberStatus;
  joinedAt: Timestamp;
  completedAt?: Timestamp;
}

// Private completion data. Stored in a separate sibling collection so that
// firestore.rules can gate visibility by group.completedCount without leaking
// it via field-level access (rules can only allow/deny whole documents).
//
// Rule: a member can read /answers/{otherUid} only when both
//   - the writer's own member doc has status === 'completed', AND
//   - the group's completedCount >= GROUP_REVEAL_MIN.
// They can always read their own answers doc.
export interface GroupMemberAnswers {
  userId: string;
  answers: Record<string, number>; // questionId -> 0..1
  cubePosition: { x: number; y: number; z: number };
  archetype: string;
  // Pointer to the member's own /quiz/results/[id] doc, written at submit
  // time. Only visible to the member themselves in the UI (we don't deep-
  // link other people's detailed dossiers from the group reveal, even though
  // the field is technically readable post-reveal — privacy is a UI choice).
  personalResultId?: string;
  completedAt: Timestamp;
}

// Shape passed to createGroup() - everything the host needs to choose at
// creation time.
export interface CreateGroupInput {
  attribution?: GroupCreationAttribution;
  name?: string;
  hostDisplayName: string;
  questionIds: string[];
  locale: 'en' | 'de';
  // Optional: groupId this is a retake-of. Persisted to the new group
  // doc so the reveal can fetch the original for shift comparison.
  retakeOf?: string;
  // Compare redesign (phase 1). 'pair' = 1:1, 'group' = N-way (default),
  // 'broadcast' = anonymous. maxParticipants must be 2 for pair.
  mode?: 'pair' | 'group' | 'broadcast';
  maxParticipants?: number;
  // When set, the host's existing solo result links into the group so
  // they don't retake: the server marks their slot completed and writes
  // their locked-set answers from this response id.
  creatorPersonalResultId?: string;
  creatorQuizType?: 'short' | 'long';
}

// Shape returned to the host after group creation.
export interface CreateGroupResult {
  groupId: string;
  inviteUrl: string;
  // Returned only to this creator, never in public group read payloads.
  attribution?: GroupCreationAttribution;
}

// Shape submitted by a member when completing the quiz inside a group.
export interface MemberCompletion {
  answers: Record<string, number>;
  cubePosition: { x: number; y: number; z: number };
  archetype: string;
  // Pointer back to the user's personal /quiz/results/[id] doc so the
  // group reveal can deep-link members to their own detailed dossier.
  personalResultId?: string;
}

// Pre-reveal "predict the group" mini-game submission. Captured in the
// lobby after a member finishes their quiz but before the reveal page
// loads, so the prediction isn't biased by seeing the answer. Stored at
// /groups/{groupId}/predictions/{userId}.
export interface GroupPrediction {
  userId: string;
  // Macro cell index 0-8. Row-major: row = social (0=progressive, 2=conservative),
  // col = economic (0=left, 1=center, 2=right). So index = row * 3 + col.
  // Matches calculateMacroCellCode's intent but as a single int.
  macroCellIndex: number;
  // Member's guess for the group's archetype string. Free-text (we
  // autocomplete from ideologyData on the client, but don't enforce).
  archetypeGuess: string;
  // Optional governance-axis guess (0 = libertarian, 1 = center, 2 =
  // authoritarian). The macro cell covers economic + social already; this
  // closes out the third axis so predictions span the full 3D cube.
  // Optional for backward compat with predictions written before this
  // field existed.
  governanceGuess?: number;
  createdAt: Timestamp;
}
